<script lang="ts">
  /**
   * Vault's library — Pattern 1 from `03-shell-and-layout.md`.
   *
   *   title + one sentence of state                    [File a document]
   *   Library · Intake 4 · Expiring · In-case · Tax year
   *   ───────────────────────────────────────────────────────────────────
   *   rows at --row-h, serif names, provenance underneath, Expires tight
   *
   * Three decisions worth stating, because each replaced something that looked
   * reasonable and read badly:
   *
   *  · **The sentence under the title states the whole library in words.** It is
   *    the part a household actually reads, and it degrades honestly — a list that
   *    failed to load is named in the sentence rather than being silently missing
   *    from it, because an absent clause reads as "nothing there".
   *  · **One number in the tab strip, and it is Intake.** Counts on every tab made
   *    the strip a dashboard, and a permanently coloured badge is wallpaper inside
   *    a week. Intake is the only one that means *somebody must press something*.
   *  · **In-case is a tab, not a search.** Two taps from the home screen: Vault,
   *    then In-case. It is the reason the portal exists — the day somebody else has
   *    to find something — and every open is written to the trail, so leaving it
   *    open costs nothing in accountability.
   */
  import { Icon, dateZA, Panel } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";
  import { expiryOf, inWords, pretty, type Doc, type ExpiringRow, type Meta } from "../lib/model";
  import DocRow from "./DocRow.svelte";
  import DocumentPage from "./DocumentPage.svelte";
  import TaxYears from "./TaxYears.svelte";
  import Ask from "./Ask.svelte";
  import FileTab from "./FileTab.svelte";
  import Waiting from "./Waiting.svelte";

  type Load<T> = { data: T | null; error: string | null; loading: boolean };

  const TABS = [
    { id: "library", label: "Library" },
    { id: "intake", label: "Intake" },
    { id: "expiring", label: "Expiring" },
    { id: "incase", label: "In-case" },
    { id: "tax", label: "Tax year" },
  ] as const;
  type TabId = (typeof TABS)[number]["id"];

  let tab = $state<TabId>("library");
  let filing = $state(false);
  let q = $state("");

  const meta = $state<Load<Meta>>({ data: null, error: null, loading: true });
  const all = $state<Load<{ docs: Doc[] }>>({ data: null, error: null, loading: true });
  const exp = $state<Load<{ days: number; rows: ExpiringRow[] }>>({ data: null, error: null, loading: true });
  const incase = $state<Load<{ docs: Doc[] }>>({ data: null, error: null, loading: false });
  /**
   * The intake count, fetched here as well as inside `Waiting`.
   *
   * The badge has to be right before the tab is ever opened — that is the whole
   * point of it — and `Waiting` only loads when its tab renders. Two small reads of
   * the same collection is the cheaper of the two available wrongs; the other is
   * hoisting the entire Drive queue into this component to save one request.
   */
  const queue = $state<Load<{ items: { status: string }[] }>>({ data: null, error: null, loading: true });

  /** The object page. `null` means the list. */
  let openId = $state<string | null>(null);

  async function fill<T>(p: Load<T>, path: string) {
    p.loading = true;
    try {
      p.data = await api.get<T>(path);
      p.error = null;
    } catch (e) {
      p.error = e instanceof ApiError ? e.message : "Unavailable";
    } finally {
      p.loading = false;
    }
  }

  /**
   * Ask for more than a screenful.
   *
   * The API caps at 500. The sentence says "and possibly more" at the cap rather
   * than presenting a truncated count as the whole library — a figure that is
   * quietly wrong is the same lie as a figure that is invented.
   */
  const LIMIT = 500;

  function docsPath(text: string) {
    return text.trim() ? `/docs?limit=${LIMIT}&q=${encodeURIComponent(text.trim())}` : `/docs?limit=${LIMIT}`;
  }

  /**
   * Everything the title sentence and the tab strip depend on, re-read after a
   * change. Called from handlers only — never from an effect, because it reads `q`
   * and an effect that tracked it would refetch the whole portal on every keystroke
   * that reaches the search box.
   */
  function refreshLists() {
    void fill(all, docsPath(q));
    void fill(exp, "/expiring?days=90");
    void fill(incase, "/incase");
    void fill(queue, "/queue");
  }

  $effect(() => {
    void fill(meta, "/meta");
    void fill(all, `/docs?limit=${LIMIT}`);
    void fill(exp, "/expiring?days=90");
    void fill(incase, "/incase");
    void fill(queue, "/queue");
  });

  /**
   * Deep links from expiry nudges land on `#doc-{id}`, so the hash opens the object
   * page directly. Before this, a nudge dropped you on a list that had not loaded
   * the document it was about — the link worked and did nothing useful.
   */
  function readHash() {
    const m = /^#doc-(.+)$/.exec(window.location.hash);
    openId = m ? m[1]! : null;
  }
  $effect(() => {
    readHash();
    window.addEventListener("hashchange", readHash);
    return () => window.removeEventListener("hashchange", readHash);
  });

  function openDoc(id: string) {
    // Through the hash rather than straight to state, so the browser Back button
    // returns to the list — which is what everyone will press.
    window.location.hash = `doc-${id}`;
  }
  function backToList() {
    // Pushed rather than cleared with `location.hash = ""`, which would leave a
    // bare "#" in the address bar and in anything anybody copies out of it.
    history.pushState(null, "", window.location.pathname + window.location.search);
    openId = null;
  }

  const intakeCount = $derived(
    queue.data ? queue.data.items.filter((i) => i.status === "waiting").length : null,
  );

  /** Expiring inside the fortnight the title sentence talks about. */
  const soon = $derived(exp.data ? exp.data.rows.filter((r) => r.daysLeft <= 14) : null);
  const overdue = $derived(soon ? soon.filter((r) => r.overdue).length : null);

  /**
   * "142 documents. Four waiting in intake. Nothing expires in the next fourteen
   * days." — the whole library, in one sentence, in words.
   */
  const sentence = $derived.by(() => {
    const parts: string[] = [];

    if (all.error) parts.push("The library could not be read.");
    else if (all.data) {
      const n = all.data.docs.length;
      if (q.trim()) parts.push(n === 0 ? `Nothing matches “${q.trim()}”.` : `${n} match “${q.trim()}”.`);
      else if (n === 0) parts.push("Nothing filed yet.");
      else parts.push(n >= LIMIT ? `${n} documents, and possibly more.` : `${n} documents.`);
    }

    if (queue.error) parts.push("Intake could not be read — that is not the same as empty.");
    else if (intakeCount !== null) {
      parts.push(
        intakeCount === 0 ? "Nothing waiting in intake." : `${inWords(intakeCount)} waiting in intake.`,
      );
    }

    if (exp.error) parts.push("The expiry list could not be read.");
    else if (soon !== null && overdue !== null) {
      if (overdue > 0) {
        parts.push(`${inWords(overdue)} ${overdue === 1 ? "has" : "have"} already expired.`);
      } else if (soon.length === 0) {
        parts.push("Nothing expires in the next fourteen days.");
      } else {
        parts.push(
          `${inWords(soon.length)} ${soon.length === 1 ? "expires" : "expire"} in the next fourteen days.`,
        );
      }
    }

    return parts.join(" ");
  });
</script>

{#if openId}
  <DocumentPage
    id={openId}
    meta={meta.data}
    onback={backToList}
    onopenDoc={openDoc}
    onchanged={refreshLists}
  />
{:else}
  <header class="page">
    <div class="page__lead">
      <h1 class="page__t">Library</h1>
      <!-- Empty until something is known. A sentence assembled from nothing reads
           as a fact about an empty house. -->
      <p class="page__s">{sentence}</p>
    </div>
    <div class="page__acts">
      <button
        class="btn-primary"
        onclick={() => {
          tab = "intake";
          filing = true;
        }}
      >
        File a document
      </button>
    </div>
  </header>

  <nav class="tabs" aria-label="Vault sections">
    {#each TABS as t (t.id)}
      <button
        class="tab"
        class:tab--on={tab === t.id}
        aria-current={tab === t.id ? "page" : undefined}
        onclick={() => (tab = t.id)}
      >
        {t.label}
        {#if t.id === "intake" && intakeCount !== null && intakeCount > 0}
          <!-- The only number in the strip, and amber because it means somebody
               must press something. Never colour alone: the title sentence above
               says "Four waiting in intake" in words on the same screen. -->
          <span class="tab__n">{intakeCount}</span>
        {/if}
      </button>
    {/each}
  </nav>

  <!-- ── Library ───────────────────────────────────────────────────────── -->
  {#if tab === "library"}
    <!-- F17 sits at the top of the library rather than in a tab of its own: the
         box you type a document's name into is the box you type a question into,
         and splitting them would make people choose before they know which they
         are doing. -->
    <Ask
      onopen={openDoc}
      onquery={(text) => {
        q = text;
        void fill(all, docsPath(text));
      }}
    />

    <div class="list">
      {@render colhead("Document", "Expires")}
      <Panel
        loading={all.loading}
        error={all.error}
        isEmpty={!all.loading && !all.error && (all.data?.docs.length ?? 0) === 0}
        onretry={() => void fill(all, `/docs?limit=${LIMIT}`)}
        rows={5}
      >
        {#snippet empty()}
          {#if q.trim()}
            Nothing matches “{q.trim()}”. Try the supplier, the policy number, or clear the box to
            see everything filed.
          {:else}
            No documents yet. Drop one in the watched Drive folder, or press <strong>File a
            document</strong> and upload it here.
          {/if}
        {/snippet}
        {#each all.data?.docs ?? [] as d (d.id)}
          <DocRow doc={d} onopen={openDoc} />
        {/each}
      </Panel>
    </div>
  {/if}

  <!-- ── Intake ────────────────────────────────────────────────────────── -->
  {#if tab === "intake"}
    {#if filing}
      <div class="filing">
        <div class="filing__head">
          <span class="kicker">File a document</span>
          <button class="lnk" onclick={() => (filing = false)}>Close</button>
        </div>
        <FileTab
          meta={meta.data}
          {pretty}
          onFiled={() => {
            refreshLists();
          }}
        />
      </div>
    {/if}

    <Waiting meta={meta.data} {pretty} onFiled={refreshLists} />
  {/if}

  <!-- ── Expiring ──────────────────────────────────────────────────────── -->
  {#if tab === "expiring"}
    <div class="list">
      {@render colhead("What expires in the next 90 days", "Expires")}
      <Panel
        loading={exp.loading}
        error={exp.error}
        isEmpty={!exp.loading && !exp.error && (exp.data?.rows.length ?? 0) === 0}
        onretry={() => void fill(exp, "/expiring?days=90")}
        rows={4}
      >
        {#snippet empty()}
          Nothing expires in the next 90 days. Documents with no expiry date are fine and simply do
          not appear here — open one and add a date if it should.
        {/snippet}
        {#each exp.data?.rows ?? [] as r (r.id)}
          {@const e = expiryOf(r.expiresAt)}
          <!-- Its own row rather than a `DocRow`: `/expiring` returns the expiry
               fields and nothing else, and padding the gaps to reuse the component
               would render "no amount" and "no checks" as facts nobody established. -->
          <button class="xrow" onclick={() => openDoc(r.id)}>
            <span class="xrow__glyph"><Icon name={r.overdue ? "alert" : "clock"} size={16} /></span>
            <span class="xrow__body">
              <span class="xrow__name">{r.title}</span>
              <span class="xrow__prov">{r.kindLabel} · {pretty(r.docType)}</span>
            </span>
            <span class="xrow__exp" class:xrow__exp--warn={e?.tone === "warn"}>
              {#if e}
                {#if e.glyph}<Icon name={e.glyph} size={12} />{/if}
                <span>{e.word}</span>
                <span class="xrow__when">{dateZA(r.expiresAt)}</span>
              {/if}
            </span>
          </button>
        {/each}
      </Panel>
      <p class="foot">
        <Icon name="info" size={12} />
        Nudges go to the Front Door at 30, 7 and 1 day, rising in urgency. Dismissing one defers it
        a week rather than clearing it — a licence disc does not stop expiring because you swiped it
        away.
      </p>
    </div>
  {/if}

  <!-- ── In-case ───────────────────────────────────────────────────────── -->
  {#if tab === "incase"}
    <div class="list">
      <p class="foot foot--top">
        <Icon name="shield" size={12} />
        Policies, medical aid, wills, ID copies. Reachable in two taps by anyone with partner
        access, and every open is written to the trail — so openness costs nothing in
        accountability.
      </p>
      {@render colhead("In case", "Expires")}
      <Panel
        loading={incase.loading}
        error={incase.error}
        isEmpty={!incase.loading && !incase.error && (incase.data?.docs.length ?? 0) === 0}
        onretry={() => void fill(incase, "/incase")}
        rows={4}
      >
        {#snippet empty()}
          Nothing is marked yet. Open any document, press <strong>Correct it</strong> and tick
          <strong>in case</strong> — anything somebody else would need to find without you.
        {/snippet}
        {#each incase.data?.docs ?? [] as d (d.id)}
          <DocRow doc={d} onopen={openDoc} />
        {/each}
      </Panel>
    </div>
  {/if}

  <!-- ── Tax year ──────────────────────────────────────────────────────── -->
  {#if tab === "tax"}
    <TaxYears onopen={openDoc} />
  {/if}
{/if}

{#snippet colhead(left: string, right: string)}
  <div class="colhead">
    <span>{left}</span>
    <span class="colhead__r">{right}</span>
  </div>
{/snippet}

<style>
  .page {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--sp-4);
    flex-wrap: wrap;
    margin-bottom: var(--sp-4);
  }
  .page__lead {
    min-width: 0;
  }
  .page__t {
    margin: 0;
    font-size: var(--fs-h1);
    font-weight: 600;
    letter-spacing: -0.01em;
    color: var(--tx);
  }
  /* The sentence a household reads. Wide measure, ordinary body face — this is
     prose, not a caption. */
  .page__s {
    margin: 6px 0 0;
    max-width: 60ch;
    font-size: var(--fs-body);
    line-height: 1.5;
    color: var(--tx2);
  }
  .page__acts {
    display: flex;
    gap: var(--sp-2);
    flex-shrink: 0;
  }
  .page__acts .btn-primary {
    padding: 9px 16px;
    font-size: var(--fs-small);
  }

  .tabs {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: var(--sp-4);
  }
  .tab {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 13px;
    border-radius: var(--r-pill);
    font-size: var(--fs-small);
    font-weight: 700;
    color: var(--mut);
    box-shadow: inset 0 0 0 1px var(--line);
  }
  .tab:hover {
    color: var(--tx2);
  }
  .tab--on {
    color: var(--tx);
    background: var(--wash);
    box-shadow: inset 0 0 0 1px var(--acc);
  }
  .tab__n {
    display: inline-grid;
    place-items: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: var(--r-pill);
    background: var(--warn);
    /* Near-black on amber in the dark themes, white on the darkened amber in
       daylight — the one ink token allowed on a filled accent. */
    color: var(--acc-ink);
    font-size: var(--fs-micro);
    font-weight: 800;
  }

  .list {
    padding: var(--sp-2) var(--sp-3) var(--sp-3);
    border-radius: var(--r-surface);
    background: var(--s1);
    box-shadow: inset 0 0 0 1px var(--line);
  }
  .colhead {
    display: flex;
    justify-content: space-between;
    gap: var(--sp-3);
    padding: var(--sp-2) var(--sp-3);
    font-size: var(--fs-micro);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--mut);
  }
  .colhead__r {
    flex-shrink: 0;
  }

  .foot {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin: var(--sp-3) 0 0;
    padding: 0 var(--sp-3);
    font-size: var(--fs-small);
    color: var(--mut);
    line-height: 1.5;
    max-width: 74ch;
  }
  .foot--top {
    margin: var(--sp-2) 0 var(--sp-3);
  }

  .filing {
    margin-bottom: var(--gap-section);
  }
  .filing__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--sp-3);
    margin-bottom: var(--sp-2);
  }

  /* ── Expiring rows ─────────────────────────────────────────────────────── */
  .xrow {
    display: flex;
    align-items: center;
    gap: var(--sp-3);
    width: 100%;
    min-height: var(--row-h);
    padding: var(--sp-2) var(--sp-3);
    text-align: left;
    border-bottom: 1px solid var(--line);
  }
  .xrow:last-child {
    border-bottom: none;
  }
  .xrow:hover {
    background: var(--fill);
  }
  .xrow__glyph {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    color: var(--mut);
  }
  .xrow__body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .xrow__name {
    font-family: var(--font-heading);
    font-size: var(--fs-lead);
    font-weight: 600;
    line-height: 1.25;
    color: var(--tx);
  }
  .xrow__prov {
    font-size: var(--fs-small);
    color: var(--mut);
  }
  .xrow__exp {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: var(--fs-small);
    color: var(--mut);
  }
  .xrow__exp--warn {
    color: var(--warn);
    font-weight: 700;
  }
  .xrow__when {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  @container panel (max-width: 560px) {
    .page__acts {
      width: 100%;
    }
    .xrow {
      flex-wrap: wrap;
      align-items: flex-start;
      padding: var(--sp-3);
    }
    .xrow__exp {
      width: 100%;
      padding-left: calc(16px + var(--sp-3));
    }
  }
</style>
